import { useEffect, useRef } from 'react';

/**
 * Reports whether an animated full-screen flow is still present. The closing
 * signal waits through the next layout frame so body-scroll restoration and
 * the map's ResizeObserver settle while listing viewport updates stay paused.
 */
export const useFlowPresenceSignal = (
  isPresent: boolean,
  onPresenceChange?: (isPresent: boolean) => void,
) => {
  const callbackRef = useRef(onPresenceChange);
  const reportedPresentRef = useRef(false);

  callbackRef.current = onPresenceChange;

  useEffect(() => {
    if (isPresent) {
      reportedPresentRef.current = true;
      callbackRef.current?.(true);
      return undefined;
    }

    if (!reportedPresentRef.current) return undefined;

    let releaseFrame: number | null = null;
    const settleFrame = window.requestAnimationFrame(() => {
      releaseFrame = window.requestAnimationFrame(() => {
        reportedPresentRef.current = false;
        callbackRef.current?.(false);
      });
    });

    return () => {
      window.cancelAnimationFrame(settleFrame);
      if (releaseFrame !== null) window.cancelAnimationFrame(releaseFrame);
    };
  }, [isPresent]);
};
